/* PŘEBYTEK, ZE KTERÉHO PLÁN POČÍTÁ — JEDNO PRAVIDLO PRO ENGINE, SOLVER I ANALÝZU.
 *
 * ZADÁNÍ MAJITELE (4. 10. 2026, Č9), doslova: „kdyz už dají volne casflow je
 * dobry tam dat kolik muzou dat ale aby to bylo ze i kdyz nevyplni kolik muzou
 * dat tak aby to vytvořrilo reseni a ne ze bez toho to nepustí dal“.
 *
 * Do 4. 10. 2026 se plán bez odsouhlaseného přebytku a bez výdajů ZASTAVIL
 * (`prebytekChybi`, hláška „doplňte výdaje“) — poradce, který výdaje na první
 * schůzce neprobral, se k řešení nedostal vůbec. Teď platí pořadí:
 *
 *   1. ODSOUHLASENÝ přebytek (vyplněný poradcem) má VŽDY přednost.
 *      Vyplněná NULA zůstává odsouhlasenou nulou → plán se zastaví a zeptá
 *      (rozhodnutí 25. 8. 2026 se nemění).
 *   2. Nevyplněno, ale známe příjmy I výdaje → volné cash flow
 *      (příjmy − výdaje − splátky). Vyjde-li nula, je to údaj z dat („nic
 *      nezbývá“) a plán se zastaví stejně jako dřív — 20 % se nevymýšlí tam,
 *      kde čísla říkají, že peníze nejsou.
 *   3. Nevyplněno a výdaje NEZNÁME → 20 % čistého příjmu (pravidlo 50/30/20),
 *      nejvýš to, co zbude po splátkách.
 *   4. Neznáme ani příjem → `null`: řešení se spočítat nedá a obrazovka řekne
 *      větou, co chybí (`chybiPrijmy`). Žádná tichá nula.
 *
 * Varianty 2 a 3 jsou MODELOVÉ hodnoty (`modelovy: true`). Klient je
 * neodsouhlasil, takže je každá obrazovka, která částku ukáže, musí označit
 * „zatím neodsouhlaseno klientem“ (components/plan/StitekPrebytku.js).
 *
 * Funkce je čistá (žádné importy) schválně: volá ji planSolver i planEngine
 * a bilance.js importuje planSolver — import odsud do bilance by udělal kruh.
 * Součty příjmů, výdajů a splátek proto počítá volající (každý svým dosavadním
 * způsobem, aby se čísla s vyplněným přebytkem nepohnula ani o korunu).
 */

/** Podíl čistého příjmu, se kterým plán počítá, když výdaje neznáme (50/30/20). */
export const PODIL_PRAVIDLA = 0.2;

/** Odkud se přebytek vzal. Kódy, ne věty — text je ve slovníku `prebytekModel`. */
export const PREBYTEK_ZDROJ = Object.freeze({
  ODSOUHLASENY: "odsouhlaseny",
  DOPOCET: "dopocet",
  PRAVIDLO: "pravidlo",
  CHYBI_PRIJMY: "chybiPrijmy",
});

/** `true` = částka je model (dopočet nebo pravidlo), ne klientův souhlas. */
export function jeModelovyPrebytek(zdroj) {
  return zdroj === PREBYTEK_ZDROJ.DOPOCET || zdroj === PREBYTEK_ZDROJ.PRAVIDLO;
}

const kladne = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {object} p
 * @param {number|null} p.zadany  přebytek z analýzy, už převedený na číslo (`null` = nevyplněno)
 * @param {number} p.prijmy  čisté příjmy domácnosti / měs
 * @param {number} p.vydaje  výdaje / měs (0 = nezadáno)
 * @param {number} p.splatky splátky úvěrů / měs
 * @returns {{castka: number|null, seZnamenkem: number|null, zdroj: string, modelovy: boolean}}
 *   `castka` ≥ 0 (nebo `null` = nedá se spočítat), `seZnamenkem` u dopočtu
 *   i se schodkem (jen pro výpočet uvolněných peněz v solveru).
 */
export function prebytekZModelu({ zadany = null, prijmy = 0, vydaje = 0, splatky = 0 } = {}) {
  const z = zadany === null || zadany === undefined || !Number.isFinite(Number(zadany)) ? null : Number(zadany);
  if (z !== null) {
    return { castka: Math.max(0, z), seZnamenkem: z, zdroj: PREBYTEK_ZDROJ.ODSOUHLASENY, modelovy: false };
  }
  const p = kladne(prijmy);
  const v = kladne(vydaje);
  const s = kladne(splatky);
  if (p > 0 && v > 0) {
    const cf = p - v - s;
    return { castka: Math.max(0, cf), seZnamenkem: cf, zdroj: PREBYTEK_ZDROJ.DOPOCET, modelovy: true };
  }
  if (p > 0) {
    /* Strop „co zbude po splátkách“: příjem 40 000 se splátkami 35 000 nemá
     * odkud dát 8 000. Bez výdajů víc nevíme, takže se bere menší z obou. */
    const pravidlo = Math.round(Math.max(0, Math.min(p * PODIL_PRAVIDLA, p - s)));
    return { castka: pravidlo, seZnamenkem: pravidlo, zdroj: PREBYTEK_ZDROJ.PRAVIDLO, modelovy: true };
  }
  return { castka: null, seZnamenkem: null, zdroj: PREBYTEK_ZDROJ.CHYBI_PRIJMY, modelovy: false };
}
